import { useQuery } from '@tanstack/react-query';

import { Individual, individualsService } from '@aslanpay/portal-common-lib';

interface PreviousEmploymentParams {
  companyId?: string;
  payCalendarId?: string;
  email?: string;
  externalPayrollId?: string;
  enabled?: boolean;
}

const findClosedEmployment = async ({
  companyId,
  payCalendarId,
  email,
  externalPayrollId,
}: {
  companyId?: string;
  payCalendarId?: string;
  email?: string[];
  externalPayrollId?: string[];
}) => {
  const response = await individualsService.fetchIndividuals({
    companyId: companyId ? [companyId] : undefined,
    payCalendarId: payCalendarId ? [payCalendarId] : undefined,
    email,
    externalPayrollId,
    status: ['LEFT', 'DELETED'],
    includeDeleted: true,
    role: ['EMPLOYEE'],
    page: 0,
    size: 1,
  });

  return response.list[0] ?? null;
};

/**
 * Finds a closed employment that an employee being created would be returning to.
 *
 * Matched on email first and payroll id second, the same order the HRIS sync uses, so the two paths agree on
 * which record a return belongs to. The two are scoped differently on purpose: an email identifies a person
 * across the whole company, but a payroll id is only unique within a pay calendar, so matching one company-wide
 * would pair the new employee with an unrelated person who happens to share the number on another calendar.
 *
 * Closed means terminal status — LEFT or DELETED. A deleted employee is offered as a previous employment just as
 * a leaver is, so someone whose record was removed can still be rehired against it.
 */
const useFetchPreviousEmployment = ({
  companyId,
  payCalendarId,
  email,
  externalPayrollId,
  enabled = true,
}: PreviousEmploymentParams) => {
  const trimmedEmail = email?.trim();
  const trimmedPayrollId = externalPayrollId?.trim();
  const hasIdentifier = !!trimmedEmail || !!trimmedPayrollId;

  return useQuery<Individual | null, Error>({
    queryKey: ['fetchPreviousEmployment', companyId, payCalendarId, trimmedEmail, trimmedPayrollId],
    queryFn: async () => {
      // company-manager's IndividualFilterParams.setEmail expects a '+' to already be the literal text
      // "%2B" by the time it reaches that setter (an API Gateway quirk) and URL-decodes on top of that —
      // a normal single-encoded '+' silently becomes a space server-side and matches nothing. client-portal
      // pre-encodes it the same way in PortalSwitcherMenuItem.
      const byEmail = trimmedEmail
        ? await findClosedEmployment({ companyId, email: [trimmedEmail.replace(/\+/g, '%2B')] })
        : null;

      if (byEmail) {
        return byEmail;
      }

      return trimmedPayrollId
        ? findClosedEmployment({ companyId, payCalendarId, externalPayrollId: [trimmedPayrollId] })
        : null;
    },
    enabled: enabled && !!companyId && hasIdentifier,
    retry: false,
  });
};

export default useFetchPreviousEmployment;
